// ============================================
// PAGE - Crear Pack
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdAdd, MdDelete, MdSave, MdClose, MdSearch, MdRemove, MdInventory2, MdInfoOutline, MdScale } from 'react-icons/md';
import { useProductosVendibles, useCrearPack, useUIStore } from '@almacenes/core';
import { InfoTooltip } from '@/components/InfoTooltip';

interface ProductoPack {
  id: number;
  nombre: string;
  cantidad: number;
  precioCompra: number;
  precioVenta: number;
}

export const CrearPackPage = () => {
  const navigate = useNavigate();
  const [codigoInterno, setCodigoInterno] = useState('');
  const [nombrePack, setNombrePack] = useState('');
  const [productos, setProductos] = useState<ProductoPack[]>([]);
  const [precioVentaFijo, setPrecioVentaFijo] = useState('');
  const [mostrarModalBusqueda, setMostrarModalBusqueda] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [errores, setErrores] = useState({
    nombrePack: '',
    productos: '',
    precioVentaFijo: '',
  });
  const [confirmacionPrecio, setConfirmacionPrecio] = useState<{ mensaje: string } | null>(null);

  // Hook para buscar productos
  const { data: productosEncontrados, isLoading: buscandoProductos } = useProductosVendibles(
    0,
    20,
    searchTerm
  );

  // Hook para crear pack
  const crearPackMutation = useCrearPack();
  const addToast = useUIStore((state) => state.addToast);

  // Scroll al inicio cuando se monta el componente
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Actualizar precio de venta fijo cuando cambien los productos
  useEffect(() => {
    const total = calcularPrecioVentaTotal();
    setPrecioVentaFijo(total.toLocaleString('es-CL'));
  }, [productos]);

  const handleVolver = () => {
    navigate('/inventario');
  };

  const handleAgregarProducto = () => {
    setMostrarModalBusqueda(true);
    setSearchInput('');
    setSearchTerm('');
  };

  const handleCerrarModal = () => {
    setMostrarModalBusqueda(false);
    setSearchInput('');
    setSearchTerm('');
  };

  const handleBuscarProducto = () => {
    if (searchInput.length >= 3) {
      setSearchTerm(searchInput);
    }
  };

  const handleSeleccionarProducto = (
    productoAlmacenId: number,
    nombre: string,
    precioCompra: number,
    precioVenta: number,
    venderPor: 'U' | 'F'
  ) => {
    // Los packs solo admiten productos de venta por unidad (la receta guarda
    // cantidades enteras; un producto a granel no tiene una "cantidad" que tenga sentido acá)
    if (venderPor === 'F') return;

    // Verificar si el producto ya está en la lista
    if (productos.find(p => p.id === productoAlmacenId)) {
      return;
    }

    setProductos([...productos, {
      id: productoAlmacenId,
      nombre,
      cantidad: 1,
      precioCompra,
      precioVenta,
    }]);
    handleCerrarModal();
  };

  const handleIncrementarCantidad = (id: number) => {
    setProductos(productos.map(p =>
      p.id === id ? { ...p, cantidad: p.cantidad + 1 } : p
    ));
  };

  const handleDecrementarCantidad = (id: number) => {
    setProductos(productos.map(p =>
      p.id === id ? { ...p, cantidad: Math.max(1, p.cantidad - 1) } : p
    ));
  };

  const handleCantidadChange = (id: number, value: string) => {
    const cantidad = parseInt(value) || 1;
    setProductos(productos.map(p =>
      p.id === id ? { ...p, cantidad: Math.max(1, cantidad) } : p
    ));
  };

  const handleEliminarProducto = (id: number) => {
    setProductos(productos.filter(p => p.id !== id));
  };

  const limpiarFormulario = () => {
    setCodigoInterno('');
    setNombrePack('');
    setProductos([]);
    setPrecioVentaFijo('');
    setErrores({
      nombrePack: '',
      productos: '',
      precioVentaFijo: '',
    });
    // Scroll al inicio
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGuardarPack = async () => {
    // Limpiar errores previos
    setErrores({
      nombrePack: '',
      productos: '',
      precioVentaFijo: '',
    });

    // Validaciones
    const nuevosErrores = {
      nombrePack: '',
      productos: '',
      precioVentaFijo: '',
    };

    let hayErrores = false;

    // Validar nombre del pack
    if (!nombrePack.trim()) {
      nuevosErrores.nombrePack = 'El nombre del pack es obligatorio';
      hayErrores = true;
    }

    // Validar que tenga al menos un producto
    if (productos.length === 0) {
      nuevosErrores.productos = 'Debes agregar al menos un producto al pack';
      hayErrores = true;
    }

    // Validar precio de venta
    const precioVenta = parseFloat(precioVentaFijo.replace(/\./g, '')) || 0;
    if (precioVenta <= 0) {
      nuevosErrores.precioVentaFijo = 'El precio de venta debe ser mayor a 0';
      hayErrores = true;
    } else {
      // Validar que el precio de venta no sea inferior al precio de compra
      // (esto sí bloquea: significaría vender bajo costo)
      const precioCompra = calcularPrecioCompraTotal();
      if (precioVenta < precioCompra) {
        nuevosErrores.precioVentaFijo = `El precio de venta no puede ser inferior al precio de compra ($${precioCompra.toLocaleString('es-CL')})`;
        hayErrores = true;
      }
      // Nota: si el precio supera la suma de precios individuales, NO se bloquea acá
      // (puede ser intencional, ej. pack premium). El servidor avisa y pide confirmación.
    }

    if (hayErrores) {
      setErrores(nuevosErrores);
      return;
    }

    await guardarPack(false);
  };

  const guardarPack = async (confirmado: boolean) => {
    try {
      const codigoPack = codigoInterno.trim()
        ? `P-${codigoInterno}`
        : nombrePack.replace(/\s+/g, '');

      const payload = {
        nombre: nombrePack,
        codigo: codigoPack,
        precio: parseFloat(precioVentaFijo.replace(/\./g, '')),
        imagen: null,
        items: productos.map(p => ({
          productoHijoId: p.id,
          cantidad: p.cantidad,
        })),
        confirmado,
      };

      await crearPackMutation.mutateAsync(payload);

      setConfirmacionPrecio(null);
      addToast('success', `Pack "${nombrePack}" creado exitosamente`);
      limpiarFormulario();
    } catch (error: any) {
      console.error('Error al crear pack:', error);

      // El precio supera la suma de precios individuales: no es un error, es una
      // advertencia que el usuario puede confirmar para continuar de todas formas.
      if (error.statusCode === 409) {
        setConfirmacionPrecio({ mensaje: error.message || 'El precio del pack es mayor a la suma de precios individuales.' });
        return;
      }

      // Verificar si hay errores de validación de campos
      let mensajeError = 'Error al crear el pack. Por favor, intenta nuevamente.';

      if (error.statusCode === 400 && error.fieldErrors) {
        const fieldErrors = error.fieldErrors;
        if (fieldErrors.length > 0) {
          // Mostrar el mensaje del primer error de campo
          mensajeError = fieldErrors[0].message;
        }
      } else if (error.message) {
        // Si no hay fieldErrors, usar el mensaje general
        mensajeError = error.message;
      }

      addToast('error', mensajeError);
    }
  };

  const calcularPrecioCompraTotal = () => {
    return productos.reduce((total, producto) => {
      return total + (producto.precioCompra * producto.cantidad);
    }, 0);
  };

  const calcularPrecioVentaTotal = () => {
    return productos.reduce((total, producto) => {
      return total + (producto.precioVenta * producto.cantidad);
    }, 0);
  };

  const calcularPorcentajeVenta = () => {
    const precioCompra = calcularPrecioCompraTotal();
    // Remover formato chileno para calcular
    const precioVentaNumerico = parseFloat(precioVentaFijo.replace(/\./g, '')) || calcularPrecioVentaTotal();

    if (precioCompra === 0 || precioVentaNumerico === 0) {
      return '0,00';
    }

    const porcentaje = ((precioVentaNumerico - precioCompra) / precioVentaNumerico) * 100;
    return porcentaje.toFixed(2).replace('.', ',');
  };

  const precioVentaTotalIndividual = calcularPrecioVentaTotal();
  const precioFijoNumerico = parseFloat(precioVentaFijo.replace(/\./g, '')) || 0;
  const diferenciaVsIndividual = precioVentaTotalIndividual - precioFijoNumerico;

  return (
    <div className="min-h-screen">
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Contenedor centrado */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 md:p-12 relative">
            {/* Botón volver flotante */}
            <button
              onClick={handleVolver}
              className="absolute top-6 left-6 p-2 rounded-lg transition-all hover:bg-gray-200"
              style={{ color: '#11194F' }}
            >
              <MdArrowBack className="text-2xl" />
            </button>

            {/* Ícono y título */}
            <div className="flex flex-col items-center mb-8">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                style={{ backgroundColor: '#EBF8FF' }}
              >
                <MdInventory2 className="text-3xl" style={{ color: '#43BFFF' }} />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-center" style={{ color: '#11194F' }}>
                Crea tu pack
              </h1>
              <p className="text-gray-500 text-center mt-1 max-w-md">
                Combina productos existentes en un solo ítem de venta, con precio fijo.
              </p>
            </div>

            {/* Formulario */}
            <div className="space-y-6">

              {/* Sección: identificación */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-5 space-y-5">
                {/* Código interno del pack */}
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Código interno del pack:
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="px-4 py-3 bg-white border-2 rounded-lg text-lg font-semibold" style={{ color: '#11194F', borderColor: '#E5E7EB' }}>
                      P-
                    </div>
                    <input
                      type="text"
                      value={codigoInterno}
                      onChange={(e) => setCodigoInterno(e.target.value)}
                      placeholder="Código interno pack"
                      className="flex-1 px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors bg-white"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Los códigos de los packs comienzan con la letra 'P-' seguida del número ingresado.
                  </p>
                </div>

                {/* Nombre del pack */}
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Nombre del pack: <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={nombrePack}
                    onChange={(e) => {
                      setNombrePack(e.target.value);
                      if (errores.nombrePack) {
                        setErrores({ ...errores, nombrePack: '' });
                      }
                    }}
                    placeholder="Nombre del producto"
                    className="w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors bg-white"
                    style={{ borderColor: errores.nombrePack ? '#EF4444' : '#E5E7EB' }}
                  />
                  {errores.nombrePack && (
                    <p className="mt-2 text-sm text-red-600">
                      {errores.nombrePack}
                    </p>
                  )}
                </div>
              </div>

              {/* Sección: productos del pack */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
                <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                  Productos del pack: <span className="text-red-600">*</span>
                </label>
                <button
                  onClick={() => {
                    handleAgregarProducto();
                    if (errores.productos) {
                      setErrores({ ...errores, productos: '' });
                    }
                  }}
                  className="w-full py-4 rounded-xl text-xl font-semibold text-white transition-all hover:opacity-90 flex items-center justify-center gap-3"
                  style={{ backgroundColor: '#10B981' }}
                >
                  <MdAdd className="text-3xl" />
                  <span>Agregar productos al pack</span>
                </button>
                <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
                  <MdInfoOutline className="text-sm mt-0.5 flex-shrink-0" />
                  <span>Solo se pueden agregar productos de venta por <strong>unidad</strong>. Los productos a granel (por peso o fracción) no se pueden incluir en un pack.</span>
                </p>
                {errores.productos && (
                  <p className="mt-2 text-sm text-red-600">
                    {errores.productos}
                  </p>
                )}

                {/* Lista de productos */}
                <div className="mt-4 rounded-xl overflow-hidden border-2" style={{ borderColor: '#E5E7EB' }}>
                  {productos.length === 0 ? (
                    <div className="px-6 py-10 text-center text-gray-500 bg-white">
                      Debe agregar productos
                    </div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: '#E5E7EB' }}>
                      {productos.map((producto) => (
                        <div key={producto.id} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 bg-white">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate" style={{ color: '#11194F' }}>
                              {producto.nombre}
                            </p>
                            <p className="text-sm text-gray-500">
                              ${producto.precioVenta.toLocaleString('es-CL')} c/u
                            </p>
                          </div>

                          <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4">
                            <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-1 py-1">
                              <button
                                onClick={() => handleDecrementarCantidad(producto.id)}
                                className="p-1.5 rounded-md hover:bg-white transition-colors"
                                style={{ color: '#11194F' }}
                              >
                                <MdRemove className="text-lg" />
                              </button>
                              <input
                                type="text"
                                value={producto.cantidad}
                                onChange={(e) => handleCantidadChange(producto.id, e.target.value)}
                                className="w-12 bg-transparent text-center font-semibold focus:outline-none"
                                style={{ color: '#11194F' }}
                              />
                              <button
                                onClick={() => handleIncrementarCantidad(producto.id)}
                                className="p-1.5 rounded-md hover:bg-white transition-colors"
                                style={{ color: '#11194F' }}
                              >
                                <MdAdd className="text-lg" />
                              </button>
                            </div>

                            <div className="w-20 sm:w-24 text-right font-semibold" style={{ color: '#11194F' }}>
                              ${(producto.precioVenta * producto.cantidad).toLocaleString('es-CL')}
                            </div>

                            <button
                              onClick={() => handleEliminarProducto(producto.id)}
                              className="p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                            >
                              <MdDelete className="text-xl text-red-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Sección: precio */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-5 space-y-5">
                {/* Precio de venta fijo */}
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Precio de venta fijo <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="text"
                      value={precioVentaFijo}
                      onChange={(e) => {
                        // Remover todo excepto números
                        const valorSinFormato = e.target.value.replace(/[^\d]/g, '');

                        // Formatear con puntos para miles
                        if (valorSinFormato === '') {
                          setPrecioVentaFijo('');
                        } else {
                          const numeroFormateado = parseInt(valorSinFormato).toLocaleString('es-CL');
                          setPrecioVentaFijo(numeroFormateado);
                        }

                        if (errores.precioVentaFijo) {
                          setErrores({ ...errores, precioVentaFijo: '' });
                        }
                      }}
                      placeholder="Precio de venta fijo"
                      className="w-full pl-8 pr-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors bg-white"
                      style={{ borderColor: errores.precioVentaFijo ? '#EF4444' : '#E5E7EB' }}
                    />
                  </div>
                  {errores.precioVentaFijo && (
                    <p className="mt-2 text-sm text-red-600">
                      {errores.precioVentaFijo}
                    </p>
                  )}

                  {/* Comparación en vivo contra la suma de precios individuales */}
                  {productos.length > 0 && precioFijoNumerico > 0 && diferenciaVsIndividual !== 0 && (
                    <p
                      className="mt-2 text-sm font-medium"
                      style={{ color: diferenciaVsIndividual > 0 ? '#10B981' : '#A5650F' }}
                    >
                      {diferenciaVsIndividual > 0
                        ? `Ahorro de $${diferenciaVsIndividual.toLocaleString('es-CL')} respecto a comprar los productos por separado (precio normal: $${precioVentaTotalIndividual.toLocaleString('es-CL')}).`
                        : `Este precio es $${Math.abs(diferenciaVsIndividual).toLocaleString('es-CL')} más caro que comprar los productos por separado (precio normal: $${precioVentaTotalIndividual.toLocaleString('es-CL')}).`}
                    </p>
                  )}
                </div>

                {/* Precio de compra, precio de venta individual y margen */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Precio de compra */}
                  <div>
                    <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                      Precio de Compra
                    </label>
                    <div className="text-2xl font-bold" style={{ color: '#11194F' }}>
                      ${calcularPrecioCompraTotal().toLocaleString('es-CL')}
                    </div>
                  </div>

                  {/* Precio de venta (suma de precios individuales) */}
                  <div>
                    <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                      Precio venta (si se vendieran aparte)
                    </label>
                    <div className="text-2xl font-bold" style={{ color: '#11194F' }}>
                      ${precioVentaTotalIndividual.toLocaleString('es-CL')}
                    </div>
                  </div>

                  {/* Margen */}
                  <div>
                    <label className="flex items-center gap-1.5 text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                      Margen
                      <InfoTooltip text="Este porcentaje se calcula sobre el precio de venta. Si vendes el pack en $1.300 y tu costo es $1.000, tu margen es 23,08% del precio de venta." />
                    </label>
                    <div className="text-2xl font-bold" style={{ color: '#11194F' }}>
                      {calcularPorcentajeVenta()}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón guardar */}
              <button
                onClick={handleGuardarPack}
                disabled={crearPackMutation.isPending}
                className="w-full py-4 rounded-xl text-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ backgroundColor: '#43BFFF' }}
              >
                {crearPackMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <MdSave className="text-2xl" />
                    <span>Añadir Pack</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de búsqueda de productos */}
      {mostrarModalBusqueda && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b-2 border-gray-200">
              <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                Buscar Producto
              </h2>
              <button
                onClick={handleCerrarModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: '#11194F' }}
              >
                <MdClose className="text-3xl" />
              </button>
            </div>

            {/* Buscador */}
            <div className="p-6 border-b-2 border-gray-200">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleBuscarProducto()}
                    placeholder="Buscar producto por nombre..."
                    className="w-full pl-12 pr-4 py-4 text-lg border-2 rounded-xl focus:outline-none focus:border-primary transition-colors"
                    style={{ borderColor: '#E5E7EB' }}
                    autoFocus
                  />
                  {searchInput.length > 0 && searchInput.length < 3 && (
                    <p className="absolute left-0 -bottom-5 text-xs text-orange-600">
                      Ingresa al menos 3 caracteres para buscar
                    </p>
                  )}
                </div>
                <button
                  onClick={handleBuscarProducto}
                  disabled={searchInput.length < 3}
                  className="px-8 py-4 rounded-xl transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#FACC15', color: '#11194F' }}
                >
                  <MdSearch className="text-2xl" />
                </button>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
                <MdInfoOutline className="text-sm flex-shrink-0" />
                <span>Solo los productos de venta por unidad se pueden agregar a un pack.</span>
              </p>
            </div>

            {/* Resultados */}
            <div className="flex-1 overflow-y-auto p-6">
              {buscandoProductos && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-gray-600">Buscando productos...</p>
                </div>
              )}

              {!buscandoProductos && searchTerm.length >= 3 && (!productosEncontrados || productosEncontrados.content.length === 0) && (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-xl text-gray-600">No se encontraron productos</p>
                  <p className="text-gray-500 mt-2">Intenta con otra búsqueda</p>
                </div>
              )}

              {!buscandoProductos && productosEncontrados && productosEncontrados.content.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 mb-4">
                    Se encontraron {productosEncontrados.content.length} productos
                  </p>
                  {productosEncontrados.content.map((producto) => {
                    const yaAgregado = productos.find(p => p.id === producto.productoAlmacenId);
                    const esGranel = producto.venderPor === 'F';
                    return (
                      <div
                        key={producto.productoAlmacenId}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-2 rounded-xl transition-colors"
                        style={{ borderColor: '#E5E7EB', opacity: esGranel ? 0.6 : 1 }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-lg truncate" style={{ color: '#11194F' }}>
                              {producto.productoNombre}
                            </p>
                            {esGranel && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 flex-shrink-0">
                                <MdScale className="text-sm" />
                                A granel
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            ${(producto.precioVentaActual || 0).toLocaleString('es-CL')}
                          </p>
                        </div>
                        <button
                          onClick={() => handleSeleccionarProducto(
                            producto.productoAlmacenId,
                            producto.productoNombre,
                            producto.precioCompraActual || 0,
                            producto.precioVentaActual || 0,
                            producto.venderPor
                          )}
                          disabled={!!yaAgregado || esGranel}
                          title={esGranel ? 'Los productos a granel no se pueden incluir en un pack' : undefined}
                          className="w-full sm:w-auto px-6 py-2 rounded-lg font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                          style={{
                            backgroundColor: yaAgregado ? '#9CA3AF' : esGranel ? '#D1D5DB' : '#10B981',
                            color: 'white'
                          }}
                        >
                          {yaAgregado ? 'Agregado' : esGranel ? 'No disponible' : 'Seleccionar'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchTerm.length < 3 && !buscandoProductos && (
                <div className="flex flex-col items-center justify-center py-12">
                  <MdSearch className="text-6xl text-gray-300 mb-4" />
                  <p className="text-xl text-gray-600">Busca un producto</p>
                  <p className="text-gray-500 mt-2">Escribe al menos 3 caracteres para ver resultados</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmación: el precio del pack supera la suma de precios individuales */}
      {confirmacionPrecio && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-3" style={{ color: '#11194F' }}>
              Este pack costará más que sus productos por separado
            </h2>
            <p className="text-gray-600 mb-6">{confirmacionPrecio.mensaje}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmacionPrecio(null)}
                className="px-4 py-2 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Revisar precio
              </button>
              <button
                onClick={() => guardarPack(true)}
                className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
                style={{ backgroundColor: '#10B981' }}
              >
                Crear igual
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
